import {FirebaseApp, initializeApp} from 'firebase/app';
import {fetchAndActivate, getRemoteConfig, getValue, RemoteConfig} from 'firebase/remote-config';
import {create} from 'zustand';
import environmentStore from '@di-shared/features/environment/environmentStore';
import {RegionType} from '@shared/cci/features/const/environment';

export interface FirebaseConfigState {
  isMaintenance: boolean;
  maintenanceMessage: string;

  globalVinWhitelist: Array<string>;
  capitalVinWhitelist: Array<string>;

  init(): Promise<void>;

  checkGlobalVinWhitelist(vin: string): boolean;

  checkCapitalVinWhitelist(vin: string): boolean;
}

const DI_MAINTENANCE_VALUE_KEY = 'diMaintenance';
const DI_WHITELIST_VALUE_KEY = 'diWhitelist';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

let app: FirebaseApp | null = null;
let remoteConfig: RemoteConfig | null = null;

try {
  app = initializeApp(firebaseConfig);
  remoteConfig = getRemoteConfig(app);
  remoteConfig.settings.minimumFetchIntervalMillis = 1000 * 60 * 5;
} catch (error) {
  app = null;
  remoteConfig = null;
}
type ServiceKey = 'kdia' | 'hdia' | 'gdia' | 'kdiw' | 'hdiw' | 'gdiw';

type MaintenanceConfig = Record<
  RegionType,
  Record<
    ServiceKey,
    {
      isMaintenance: boolean;
      maintenanceMessage: string;
    }
  >
>;

const useFirebaseStore = create<FirebaseConfigState>((set, get) => ({
  isMaintenance: false,
  maintenanceMessage: '',
  globalVinWhitelist: [],
  capitalVinWhitelist: [],

  async init() {
    if (!remoteConfig) {
      set({
        isMaintenance: false,
        maintenanceMessage: '',
        globalVinWhitelist: [],
        capitalVinWhitelist: [],
      });
      return;
    }
    try {
      await fetchAndActivate(remoteConfig);

      const maintenanceValue = getValue(remoteConfig, DI_MAINTENANCE_VALUE_KEY);
      /**
       * [2025-11-10:Jeyoung Lee] FIXME: 아래는 예시 포맷이므로 firebase 적용시 삭제
       */
      // const allMaintenanceConfig: MaintenanceConfig = {
      //   KR: {
      //     kdia: {
      //       isMaintenance: false,
      //       maintenanceMessage: '점검중입니다.\n잠시 후 다시 시도해주세요.2',
      //     },
      //     hdia: {
      //       isMaintenance: false,
      //       maintenanceMessage: '점검중입니다.',
      //     },
      //     gdia: {
      //       isMaintenance: false,
      //       maintenanceMessage: '점검중입니다.',
      //     },
      //     kdiw: {
      //       isMaintenance: false,
      //       maintenanceMessage: '점검중입니다.',
      //     },
      //     hdiw: {
      //       isMaintenance: false,
      //       maintenanceMessage: '점검중입니다.',
      //     },
      //     gdiw: {
      //       isMaintenance: false,
      //       maintenanceMessage: '점검중입니다.',
      //     },
      //   },
      //   EU: {
      //     kdia: {
      //       isMaintenance: false,
      //       maintenanceMessage: '점검중입니다.\n잠시 후 다시 시도해주세요.2',
      //     },
      //     hdia: {
      //       isMaintenance: false,
      //       maintenanceMessage: '점검중입니다.',
      //     },
      //     gdia: {
      //       isMaintenance: false,
      //       maintenanceMessage: '점검중입니다.',
      //     },
      //     kdiw: {
      //       isMaintenance: false,
      //       maintenanceMessage: '점검중입니다.',
      //     },
      //     hdiw: {
      //       isMaintenance: false,
      //       maintenanceMessage: '점검중입니다.',
      //     },
      //     gdiw: {
      //       isMaintenance: false,
      //       maintenanceMessage: '점검중입니다.',
      //     },
      //   },
      // };

      const allMaintenanceConfig = JSON.parse(maintenanceValue.asString()) as MaintenanceConfig;
      const region = environmentStore.getState().region;
      const maintenanceConfig = allMaintenanceConfig[region][import.meta.env.VITE_PLATFORM as ServiceKey];

      const whitelistValue = getValue(remoteConfig, DI_WHITELIST_VALUE_KEY);
      const whitelistConfig = JSON.parse(whitelistValue.asString()) as {
        globalVinWhitelist: Array<string>;
        capitalVinWhitelist: Array<string>;
      };

      set({
        isMaintenance: maintenanceConfig.isMaintenance || false,
        maintenanceMessage: maintenanceConfig.maintenanceMessage || '',
        globalVinWhitelist: whitelistConfig.globalVinWhitelist || [],
        capitalVinWhitelist: whitelistConfig.capitalVinWhitelist || [],
      });
    } catch (e) {
      // firebase 조회실패는 무시
    }
  },
  checkGlobalVinWhitelist(vin: string) {
    return get().globalVinWhitelist.includes(vin);
  },
  checkCapitalVinWhitelist(vin: string) {
    return get().capitalVinWhitelist.includes(vin);
  },
}));

export default useFirebaseStore;
